import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ELEMENTS_TO_INTERACT_WITH } from '../../../static/constants.js';
import {
  ForgeButton,
  ForgeDialog,
  ForgeScaffold,
  ForgeToolbar,
  ForgeIcon,
} from '@tylertech/forge-react';
import ForgeStack from '../ForgeStack/ForgeStack.js';
import './ForgeDialogWrapper.scss';

const ForgeDialogWrapper = ({
  open,
  title,
  children,
  primaryButtonText,
  secondaryButtonText,
  tetriaryButtonText,
  primaryButtonDisabled = false,
  secondaryButtonDisabled = false,
  tetriaryButtonDisabled = false,
  handleClickPrimaryButton,
  handleClickSecondaryButton,
  handleClickTetriaryButton,
  handleClose,
  handleGoBack = () => { },
  showPrimaryButton = true,
  showSecondaryButton = true,
  showTetriaryButton = false,
  showGoBackButton = false,
  initiallyNotDisabled = false,
  mode = 'modal',
  classes,
  interactionNeeded = true,
  maxHeight = false,
  noDivider = false,
}) => {
  const [hasInteracted, setHasInteracted] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (open) {
      setHasInteracted(false);
    }
  }, [open]);

  useEffect(() => {
    if (hasInteracted) return;

    const el = contentRef.current;
    if (!el) return;

    const handleInteraction = () => {
      setHasInteracted(true);
    };

    el.addEventListener('input', handleInteraction, {
      once: true,
      capture: true,
    });
    el.addEventListener('change', handleInteraction, {
      once: true,
      capture: true,
    });
    el.addEventListener('click', (e) => ELEMENTS_TO_INTERACT_WITH.includes(e.target.tagName) && !hasInteracted && handleInteraction(), {
      capture: true
    });
    el.addEventListener('forge-autocomplete-change', handleInteraction, {
      once: true,
      capture: true,
    });
    el.addEventListener('forge-autocomplete-select', handleInteraction, {
      once: true,
      capture: true,
    });

    return () => {
      el.removeEventListener('input', handleInteraction, { capture: true });
      el.removeEventListener('change', handleInteraction, { capture: true });
      el.removeEventListener('click', handleInteraction, { capture: true });
      el.removeEventListener('forge-autocomplete-change', handleInteraction, { capture: true });
      el.removeEventListener('forge-autocomplete-select', handleInteraction, { capture: true });
    };
  }, [hasInteracted]);

  const isPrimaryDisabled =
    !hasInteracted && !primaryButtonDisabled && interactionNeeded ? true : primaryButtonDisabled;

  return (
    <ForgeDialog
      className={`forge-dialog ${classes}`}
      persistent
      variant="raised"
      mode={mode}
      open={open}
      animationType="slide"
    >
      <ForgeScaffold>
        <ForgeToolbar slot={'header'} no-divider={noDivider}>
          {showGoBackButton && (
            <ForgeIcon
              slot="before-start"
              name="arrow_back"
              onClick={handleGoBack}
              style={{ cursor: 'pointer', marginLeft: '12px' }}
            />
          )}
          <h2 slot="start" className="forge-typography--heading4">
            {title}
          </h2>
          <ForgeIcon
            slot="end"
            name="close"
            onClick={handleClose}
            style={{ cursor: 'pointer' }}
            className="defaultIconColor"
          />
        </ForgeToolbar>

        <div className={`dialog-content ${maxHeight ? 'max-height-content' : ''}`} slot="body" ref={contentRef}>
          {children}
        </div>

        <ForgeToolbar slot="footer" inverted no-divider={noDivider}>
          <div slot="start">
            {showTetriaryButton && (
              <ForgeButton 
                variant="text"
                disabled={tetriaryButtonDisabled}
                onClick={handleClickTetriaryButton}
              >
                {tetriaryButtonText}
              </ForgeButton>
            )}
          </div>
          <div slot="end">
            <ForgeStack inline>
              {showSecondaryButton && (
                <ForgeButton
                  variant="outlined"
                  disabled={secondaryButtonDisabled}
                  onClick={handleClickSecondaryButton}
                >
                  {secondaryButtonText}
                </ForgeButton>
              )}
              {showPrimaryButton && (
                <ForgeButton
                  variant="raised"
                  disabled={initiallyNotDisabled ? false : isPrimaryDisabled}
                  onClick={handleClickPrimaryButton}
                >
                  {primaryButtonText}
                </ForgeButton>
              )}
            </ForgeStack>
          </div>
        </ForgeToolbar>
      </ForgeScaffold>
    </ForgeDialog>
  );
};

ForgeDialogWrapper.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  children: PropTypes.node,
  primaryButtonText: PropTypes.string,
  secondaryButtonText: PropTypes.string,
  primaryButtonDisabled: PropTypes.bool,
  secondaryButtonDisabled: PropTypes.bool,
  handleClickPrimaryButton: PropTypes.func,
  handleClickSecondaryButton: PropTypes.func,
  handleClose: PropTypes.func,
  handleGoBack: PropTypes.func,
  showPrimaryButton: PropTypes.bool,
  showSecondaryButton: PropTypes.bool,
  showGoBackButton: PropTypes.bool,
  mode: PropTypes.string,
  classes: PropTypes.string,
  initiallyNotDisabled: PropTypes.bool,
  interactionNeeded: PropTypes.bool,
  maxHeight: PropTypes.bool,
  noDivider: PropTypes.bool,
  tetriaryButtonText: PropTypes.string,
  tetriaryButtonDisabled: PropTypes.bool,
  handleClickTetriaryButton: PropTypes.func,
  showTetriaryButton: PropTypes.bool,
};

export default ForgeDialogWrapper;
